'use client'
import { Loading } from '@/components/Loading'
import { SegmentControl } from '@/components/SegmentControl'
import { EmailVerified } from '@/domains/Account/modules/Auth/components/EmailVerified'
import { mainStore } from '@/domains/Account/store'
import { Icon } from '@/icons'
import { BackButton } from '@/shared/Auth/components/BackButton'
import { ConfirmResetPassword } from '@/shared/Auth/modules/ConfirmResetPassword'
import { Login } from '@/shared/Auth/modules/Login'
import { PasswordReset } from '@/shared/Auth/modules/PasswordReset'
import { PasswordResetRequest } from '@/shared/Auth/modules/PasswordResetRequest'
import { Registration } from '@/shared/Auth/modules/Registration'
import {
  clearStoredReturnUrl,
  getSafeReturnUrl,
  getStoredReturnUrl,
  storeReturnUrl,
} from '@/shared/Auth/utils/returnUrl'
import { SCREEN_LIST, TabsOptions } from '@/store/Auth'
import { reaction, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { redirect, useRouter, useSearchParams } from 'next/navigation'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import styles from './Auth.module.scss'
import { BenefitsList } from './components/BenefitsList'

const authStore = mainStore.auth

const DEFAULT_RETURN_URL = '/application/list'

interface AuthProps {
  screen: SCREEN_LIST
}
export const Auth: FunctionComponent<AuthProps> = observer(({ screen }) => {
  const prevScreen = useRef<SCREEN_LIST | null>(null)

  const router = useRouter()
  const searchParams = useSearchParams()

  if (prevScreen.current !== screen) {
    prevScreen.current = screen
    runInAction(() => authStore.setScreen(screen))
  }

  const returnUrlQueryParam = useMemo(() => {
    return searchParams.get('returnUrl')
  }, [searchParams])

  useEffect(() => {
    if (returnUrlQueryParam != null) {
      storeReturnUrl(returnUrlQueryParam)
    }
  }, [returnUrlQueryParam])

  useEffect(() => {
    const cancelReaction = reaction(
      () => authStore.screen,
      (screen) => {
        router.push('/auth/' + screen.toLowerCase())
      }
    )
    return () => cancelReaction()
  }, [router])

  const login = useCallback(async () => {
    const response = await authStore.login()
    if (response.status) {
      authStore.resetData()

      const returnUrlLocalStorage = getStoredReturnUrl()
      clearStoredReturnUrl()

      const returnUrl =
        getSafeReturnUrl(returnUrlQueryParam) ??
        returnUrlLocalStorage ??
        DEFAULT_RETURN_URL

      router.push(returnUrl)
    }
  }, [router, returnUrlQueryParam])

  const registration = useCallback(() => {
    authStore.registration()
  }, [])

  const saveNewPassword = useCallback(async () => {
    try {
      const { status } = await authStore.saveNewPassword()
      if (status) {
        authStore.setScreen(SCREEN_LIST.LOGIN)
      }
    } catch {}
  }, [])

  // Экраны EMAIL_NOT_VERIFIED и REGISTRATION_SUCCESS осмысленны только
  // сразу после логина/регистрации: email для них берётся из формы.
  // При перезагрузке или прямом заходе по URL форма пуста — редиректим
  // на логин: при SSR это HTTP 307 (без отрисовки экрана), при клиентской
  // навигации — переход с replace-семантикой.
  const isVerificationScreenWithoutEmail =
    (screen === SCREEN_LIST.EMAIL_NOT_VERIFIED ||
      screen === SCREEN_LIST.REGISTRATION_SUCCESS) &&
    authStore.form.email === ''

  if (isVerificationScreenWithoutEmail) {
    // returnUrl (например, от guard-а ЛК) переносим на экран логина,
    // чтобы после входа вернуться на исходную страницу
    const returnParam =
      returnUrlQueryParam != null
        ? `?returnUrl=${encodeURIComponent(returnUrlQueryParam)}`
        : ''

    redirect(`/auth/login${returnParam}`)
  }

  if (authStore.loadingPage) {
    return <Loading isFullscreen />
  }

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <Icon name="logo" className={styles.logo} />

        {authStore.screen === SCREEN_LIST.PASSWORD_RESET_REQUEST ||
        authStore.screen === SCREEN_LIST.PASSWORD_RESET_CONFIRM ||
        authStore.screen === SCREEN_LIST.PASSWORD_RESET ? (
          <BackButton onClick={authStore.goToLogin} />
        ) : null}

        {authStore.screen === SCREEN_LIST.LOGIN ||
        authStore.screen === SCREEN_LIST.REGISTRATION ? (
          <SegmentControl
            items={TabsOptions}
            onChange={authStore.setScreen}
            value={authStore.screen}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.LOGIN ? (
          <Login
            isLoading={authStore.loading}
            email={authStore.form.email}
            password={authStore.form.password}
            changeEmail={authStore.setEmail}
            changePassword={authStore.setPassword}
            errors={authStore.validator.errors}
            onLogin={login}
            goToResetPassword={authStore.goToResetPassword}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.REGISTRATION ? (
          <Registration
            isLoading={authStore.loading}
            name={authStore.form.name}
            phone={authStore.form.phone}
            password={authStore.form.password}
            email={authStore.form.email}
            acceptPolicy={authStore.form.acceptPolicy}
            changeName={authStore.setName}
            changePhone={authStore.setPhone}
            changePassword={authStore.setPassword}
            changeEmail={authStore.setEmail}
            changeAcceptPolicy={authStore.setAcceptPolicy}
            errors={authStore.validator.errors}
            onRegistration={registration}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.PASSWORD_RESET_REQUEST ? (
          <PasswordResetRequest
            isLoading={authStore.loading}
            email={authStore.form.email}
            changeEmail={authStore.setEmail}
            errors={authStore.validator.errors}
            resetPassword={authStore.passwordResetRequest}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.PASSWORD_RESET_CONFIRM ? (
          <ConfirmResetPassword
            email={authStore.form.email}
            code={authStore.confirmResetPasswordCode}
            setCode={authStore.setConfirmResetPasswordCode}
            error={authStore.confirmResetPasswordError}
            onRequestRetry={authStore.passwordResetRequest}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.PASSWORD_RESET ? (
          <PasswordReset
            isLoading={authStore.loading}
            newPassword={authStore.newPassword}
            setNewPassword={authStore.setNewPassword}
            retryNewPassword={authStore.retryNewPassword}
            setRetryNewPassword={authStore.setRetryNewPassword}
            saveNewPassword={saveNewPassword}
            errors={authStore.validator.errors}
          />
        ) : null}

        {authStore.screen === SCREEN_LIST.REGISTRATION_SUCCESS ? (
          <EmailVerified email={authStore.form.email} isSuccessRegistration />
        ) : null}

        {authStore.screen === SCREEN_LIST.EMAIL_NOT_VERIFIED ? (
          <EmailVerified email={authStore.form.email} />
        ) : null}
      </div>
      <div className={styles.rightColumn}>
        <BenefitsList />
      </div>
    </div>
  )
})
